import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  getCurrentMonthYear as getMonthYear,
  navigateMonth as navMonth,
  getCalendarDays as getDays,
  isToday as checkToday,
  isDayInSelectedWeek as checkDayInSelectedWeek,
  getWeekStart,
} from '../../utils/calendar.util';

export interface PeriodOption {
  id: string;
  label: string;
  value: string;
}

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Format a week-boundary date. End dates use the short month name; start dates use the full month name. */
function formatWeekDate(date: Date, isEnd = false): string {
  const month = isEnd ? MONTHS_SHORT[date.getMonth()] : MONTHS_FULL[date.getMonth()];
  return `${WEEK_DAYS[date.getDay()]}, ${date.getDate()} ${month} ${date.getFullYear()}`;
}

export interface CustomWeekData {
  startDate: string;
  endDate: string;
  weekRange: string;
}

/**
 * PeriodDropdownComponent - A reusable dropdown for selecting predefined periods or a custom week range with an inline week calendar.
 * This component provides a selectable list of period options, supports staging a temporary selection before applying,
 * offers a clear/cancel workflow, and includes a custom-week calendar that emits normalized week range data. It also
 * manages mobile behavior (locks body scroll when open) and handles outside clicks to close sub-panels safely.
 * @component
 * @selector app-period-dropdown
 * @example
 *   <app-period-dropdown
 *     [isOpen]="true"
 *     [options]="periodOptions"
 *     [selectedPeriod]="'last_7_days'"
 *     [placeholder]="'Select period'"
 *     (apply)="onApply($event)"
 *     (customWeekChange)="onCustomWeekChange($event)">
 *   </app-period-dropdown>
 *
 * Inputs:
 * @input {boolean} isOpen - Whether the dropdown is currently open.
 * @input {PeriodOption[]} options - Array of period option objects { id, label } shown in the dropdown.
 * @input {string} selectedPeriod - Currently selected period (can be an id or a label).
 * @input {string} placeholder - Placeholder text when no selection exists.
 * @input {boolean} showClear - Whether to show a "Clear" control.
 * @input {string} clearText - Label text for the clear control.
 * @input {string} primaryButtonText - Label for the primary action button (e.g., "Apply").
 * @input {string} secondaryButtonText - Label for the secondary action button (e.g., "Cancel").
 * @input {CustomWeekData | null} customWeekData - Optional external custom week data to initialize the week calendar display.
 *
 * Outputs:
 * @output {EventEmitter<void>} toggle - Emitted when the main dropdown toggle is activated (open/close intent).
 * @output {EventEmitter<string>} select - Emitted when a period option is selected (passes option id).
 * @output {EventEmitter<string>} apply - Emitted when the staged selection is applied (passes applied option id).
 * @output {EventEmitter<void>} clear - Emitted when the selection is cleared.
 * @output {EventEmitter<void>} close - Emitted when the user cancels/requests close without applying.
 * @output {EventEmitter<CustomWeekData>} customWeekChange - Emitted when a custom week is selected or changed; payload contains normalized startDate, endDate (yyyy-mm-dd) and a human-readable weekRange.
 *
 * Key methods:
 * - onToggle(): Initializes a temporary selection (tempSelectedPeriod) from current state and emits toggle to request open/close.
 * - onSelectOption(optionId): Stages an option selection and emits select.
 * - onApply(): Commits tempSelectedPeriod as selectedPeriod, emits apply, and closes the dropdown.
 * - onClear(): Clears the staged selection, emits clear, and closes the dropdown.
 * - onCancel(): Reverts staged selection to the current selectedPeriod, emits close, and closes the dropdown.
 * - isApplyDisabled(): Returns true when no change would result from applying (disables Apply button).
 * - getSelectedLabel(): Resolves the human-readable label for the current selection or returns the placeholder.
 * - toggleWeekCalendar(): Opens/closes the inline week calendar and initializes its navigation state.
 * - selectWeek(date): Selects a week for the provided date, emits customWeekChange with formatted start/end and weekRange, and closes the week calendar.
 * - updateCustomWeekData(): Internal helper to compute start/end of the week and emit customWeekChange for selectedDate.
 * - getCalendarDays(), navigateMonth(), getCurrentMonthYear(), isToday(), isDayInSelectedWeek(): Calendar helpers used to render and navigate the week picker.
 * - onDocumentClick(event): Host listener that closes the dropdown or week calendar appropriately when clicking outside, with safeguards for calendar/backdrop elements.
 *
 * Lifecycle hooks and behavior:
 * - ngOnChanges(changes: SimpleChanges): Watches isOpen to lock/unlock body scrolling on small screens when the dropdown opens/closes.
 * - ngOnDestroy(): Ensures body scroll is restored when the component is destroyed.
 * - @HostListener('document:click', ['$event']): Handles outside clicks to close the dropdown or week calendar while avoiding unintended closures for calendar/backdrop interactions.
 */
@Component({
  selector: 'app-period-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './period-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./period-dropdown.component.scss'],
})
export class PeriodDropdownComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() options: PeriodOption[] = [];
  @Input() selectedPeriod = '';
  @Input() placeholder = 'Select period';
  @Input() showClear = true;
  @Input() clearText = 'Clear';
  @Input() primaryButtonText = 'Apply';
  @Input() secondaryButtonText = 'Cancel';
  @Input() customWeekData: CustomWeekData | null = null;

  @Output() toggle = new EventEmitter<void>();
  @Output() select = new EventEmitter<string>();
  @Output() apply = new EventEmitter<string>();
  @Output() clear = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() customWeekChange = new EventEmitter<CustomWeekData>();

  tempSelectedPeriod = '';
  selectedDate: Date | null = null;

  // Custom week calendar properties
  showWeekCalendar = false;
  selectedWeekStart: Date | null = null;
  currentWeek: Date = new Date();
  dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  /**
   * Handle document click events to control period dropdown and week calendar visibility.
   * @example
   * onDocumentClick(new MouseEvent('click'))
   * undefined
   * @param {Event} event - The document click event used to determine which element was clicked.
   * @returns {void} No return value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Don't close if clicking on the custom week calendar dropdown
    if (target.closest('.week-calendar-dropdown')) {
      return;
    }

    // Don't close if clicking on mobile backdrop
    if (target.closest('.mobile-backdrop')) {
      return;
    }

    // Close the week calendar if clicking outside but keep the main dropdown open
    if (this.showWeekCalendar && !target.closest('.week-input-wrapper')) {
      this.showWeekCalendar = false;
      return;
    }

    if (!target.closest('.period-dropdown')) {
      this.closeDropdown();
    }
  }

  /**
   * Handle input changes and lock body scroll on mobile when the dropdown open state changes.
   * @example
   * gOnChanges({ isOpen: { currentValue: true, previousValue: false } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing changed input properties; this function checks the 'isOpen' change.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && window.innerWidth <= 768) {
      document.body.style.overflow = changes['isOpen'].currentValue ? 'hidden' : '';
    }
  }

  ngOnDestroy(): void {
    // Ensure body scroll is restored when component is destroyed
    document.body.style.overflow = '';
  }

  /**
   * Toggle the temporary selected period based on the current selectedPeriod (matching by label or id) and emit the toggle event.
   * @example
   * nToggle()
   * // returns void (updates tempSelectedPeriod and emits toggle)
   * @returns {void} Updates tempSelectedPeriod to a matching option id (or the first option / empty string) and emits the toggle event.
   **/
  onToggle(): void {
    // Find the option that matches the selectedPeriod (which could be a label)
    const matchingOption = this.options.find(
      option => option.label === this.selectedPeriod || option.id === this.selectedPeriod
    );

    if (matchingOption) {
      this.tempSelectedPeriod = matchingOption.id;
    } else {
      // Fallback to the first option if no match found
      this.tempSelectedPeriod = this.options.length > 0 ? this.options[0].id : '';
    }

    this.toggle.emit();
  }

  onSelectOption(optionId: string): void {
    this.tempSelectedPeriod = optionId;
    this.select.emit(optionId);
  }

  onDateSelect(event: any): void {
    if (event && event instanceof Date) {
      this.selectedDate = event;
      this.updateCustomWeekData();
    }
  }

  onApply(): void {
    this.selectedPeriod = this.tempSelectedPeriod;
    this.apply.emit(this.tempSelectedPeriod);
    this.closeDropdown();
  }

  onClear(): void {
    this.tempSelectedPeriod = '';
    this.clear.emit();
    this.closeDropdown();
  }

  onCancel(): void {
    this.tempSelectedPeriod = this.selectedPeriod;
    this.close.emit();
    this.closeDropdown();
  }

  private closeDropdown(): void {
    this.isOpen = false;
  }

  isApplyDisabled(): boolean {
    // Find the current selected option ID
    const currentOption = this.options.find(
      option => option.label === this.selectedPeriod || option.id === this.selectedPeriod
    );
    const currentOptionId = currentOption?.id || '';

    return this.tempSelectedPeriod === currentOptionId;
  }

  getSelectedLabel(): string {
    // If selectedPeriod is already a label, return it directly
    if (this.selectedPeriod && this.options.some(option => option.label === this.selectedPeriod)) {
      return this.selectedPeriod;
    }

    // Otherwise, find the option by ID
    const selectedOption = this.options.find(option => option.id === this.selectedPeriod);
    return selectedOption ? selectedOption.label : this.placeholder;
  }

  /**
   * Compute and emit the formatted custom week (start and end dates and display range) for the currently selectedDate.
   * @example
   * updateCustomWeekData()
   * Emits a CustomWeekData object via this.customWeekChange
   * @param {void} none - No arguments.
   * @returns {void} Emits a CustomWeekData event; does not return a value.
   **/
  private updateCustomWeekData(): void {
    if (!this.selectedDate) return;

    // Find the start of the week (Sunday) for the selected date
    const startOfWeek = new Date(this.selectedDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const weekRange = `${formatWeekDate(startOfWeek)} - ${formatWeekDate(endOfWeek, true)}`;

    // Format dates in local timezone to avoid timezone conversion issues
    const formatDateForAPI = (date: Date): string => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const customWeekData: CustomWeekData = {
      startDate: formatDateForAPI(startOfWeek),
      endDate: formatDateForAPI(endOfWeek),
      weekRange: weekRange,
    };

    this.customWeekChange.emit(customWeekData);
  }

  getCustomWeekDisplayText(): string {
    if (this.customWeekData) {
      return this.customWeekData.weekRange;
    }
    return 'Select custom week';
  }

  /**
   * Get the start date (Sunday) of the current week formatted as "dd/mm/yyyy".
   * @example
   * etCurrentWeekStartDate()
   * "15/03/2026"
   * @returns {string} Start date of the current week in dd/mm/yyyy format.
   **/
  getCurrentWeekStartDate(): string {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);

    // Format as dd/mm/yyyy
    const day = startOfWeek.getDate().toString().padStart(2, '0');
    const month = (startOfWeek.getMonth() + 1).toString().padStart(2, '0');
    const year = startOfWeek.getFullYear();

    return `${day}/${month}/${year}`;
  }

  // Custom Week Calendar Methods
  /**
   * Toggle the visibility of the week calendar and initialize or update currentWeek and selectedWeekStart based on any existing selection or provided custom week data.
   * @example
   * oggleWeekCalendar()
   * undefined
   * @returns {{void}} No return value.
   */
  toggleWeekCalendar(): void {
    this.showWeekCalendar = !this.showWeekCalendar;

    if (this.showWeekCalendar) {
      // If a week is already selected, navigate to that month
      if (this.selectedWeekStart) {
        this.currentWeek = new Date(this.selectedWeekStart);
      } else if (this.customWeekData) {
        // If we have customWeekData from parent, use it
        const startDate = new Date(this.customWeekData.startDate);
        this.selectedWeekStart = startDate;
        this.currentWeek = new Date(startDate);
      } else {
        // Otherwise, start with current week
        this.currentWeek = new Date();
        const dayOfWeek = this.currentWeek.getDay();
        this.selectedWeekStart = new Date(this.currentWeek);
        this.selectedWeekStart.setDate(this.currentWeek.getDate() - dayOfWeek);
      }
    }
  }

  getCurrentMonthYear(): string {
    return getMonthYear(this.currentWeek);
  }

  navigateMonth(direction: 'prev' | 'next'): void {
    this.currentWeek = navMonth(this.currentWeek, direction);
  }

  getCalendarDays(): any[] {
    return getDays(this.currentWeek);
  }

  isToday(date: Date): boolean {
    return checkToday(date);
  }

  isDayInSelectedWeek(date: Date): boolean {
    return checkDayInSelectedWeek(date, this.selectedWeekStart);
  }

  /**
   * Selects the week that contains the provided date, updates component state, emits a CustomWeekData event, and closes the week calendar.
   * @example
   * electWeek(new Date('2026-03-01'))
   * void
   * @param {{Date}} {{selectedDate}} - Date used to determine the start of the selected week.
   * @returns {{void}} No value returned; side effects update component state, emit a customWeekChange event, and hide the week calendar.
   **/
  selectWeek(selectedDate: Date): void {
    const weekStart = getWeekStart(selectedDate);
    this.selectedWeekStart = weekStart;
    this.selectedDate = selectedDate;

    // Update the week data and close the calendar
    const endOfWeek = new Date(weekStart);
    endOfWeek.setDate(weekStart.getDate() + 6);

    const weekRange = `${formatWeekDate(weekStart)} - ${formatWeekDate(endOfWeek, true)}`;

    const formatDateForAPI = (date: Date): string => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const customWeekData: CustomWeekData = {
      startDate: formatDateForAPI(weekStart),
      endDate: formatDateForAPI(endOfWeek),
      weekRange: weekRange,
    };

    this.customWeekChange.emit(customWeekData);
    this.showWeekCalendar = false;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
